import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { endOfDay, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DialogEditMaintenanceComponent } from 'app/main/maintenances/dialogs/dialog-edit-maintenance/dialog-maintenances.component';
import { isEqual } from 'lodash';
import { merge, Subject } from 'rxjs';
import { debounceTime, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { sentry } from 'sentry';
import { FilterComponent } from './filter/filter.component';
import { RatingsResolverService } from './services/ratings.resolver.service';

import { ActivatedRoute, Router } from '@angular/router';
import { ChartDateIntervalSelectorMapperService } from '@core/components/charts/chart-date-interval-selector/services/chart-date-interval-selector.mapper.service';
import { LineChartResultItem } from '@core/components/charts/line/services/line.mapper.service';
import { DashboardRatingsItem, DashboardRatingsSubject, DashboardRatingTasksResult, RatingsLastOpenedAtSubject } from '@core/interfaces/reports/ratings';
import { delay } from '../../../core/utils/delay';
import { ReportsService } from '../reports.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { RatingsService } from './services/api/ratings-api.service';
import { RatingsDashboardStars } from './services/ratings.mapper.service';

@Component({
  selector: 'app-ratings',
  templateUrl: './ratings.component.html',
  styleUrls: ['./ratings.component.scss']
})
export class RatingsComponent implements OnInit, OnDestroy {

  @ViewChild(FilterComponent, { static: true })
  public filter: FilterComponent;
  public isLoading = true;
  public isLoadingOnlyResponseChart = true;
  public isFiltered = false;
  public dashboardRating: DashboardRatingsItem;
  public ratingsByQuantity: RatingsDashboardStars[];
  public accountRatingsAverageByTimeline: LineChartResultItem[];
  public bestRatedTasks: DashboardRatingTasksResult;
  public worstRatedTasks: DashboardRatingTasksResult;
  public lastOpenedAtResponse: string;
  public lastOpenedAtChartChanges = new Subject<RatingsLastOpenedAtSubject>();
  public hasEditMaintenancePermission = this.authenticationService.hasPermission('maintenances-edit');
  public byQuantityGraphTotalValue: number;
  public averageByTimelineGraphTotalValue: number;
  public lineChartData: any;

  private readonly removeSubscriptions = new Subject<void>();
  private readonly localStorageFilterValuesKey = 'ratings-dashboard-filter';

  private intervalID: NodeJS.Timer;
  private autoReloadTime = 2 * 60000;

  constructor(
    private readonly chartDateIntervalSelectorMapperService: ChartDateIntervalSelectorMapperService,
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly resolverService: RatingsResolverService,
    private readonly maintenancesApi: MaintenancesApi,
    private readonly matDialog: MatDialog,
    public readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly reportsService: ReportsService,
    private readonly authenticationService: AuthenticationService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public ngOnInit(): void {
    this.reportsService.preserveSavedFilterValues(
      this.localStorageFilterValuesKey,
      this.filter.filterForm,
      this.filter.defaultFilterValues
    );

    this.filter.filterForm.valueChanges.pipe(
      takeUntil(this.removeSubscriptions),
      filter(() => this.filter.filterForm.valid),
      debounceTime(200),
      switchMap(async () => {
        const where = this.toQuery(this.filter.filterForm.value);
        await this.updateData(where);
        return where;
      }),
    ).subscribe((where) => {
      this.isFiltered = !isEqual(this.filter.filterForm.value, this.filter.defaultFilterValues);
      if (this.isFiltered) {
        this.setUrlAfterFilter(where);
      }
    });

    this.reload();
  }

  public ngOnDestroy(): void {
    if (!this.isFiltered) {
      return;
    }

    localStorage.setItem(this.localStorageFilterValuesKey, JSON.stringify(this.filter.filterForm.value));
  }

  private setUrlAfterFilter(where): void {
    const queryParams = this.reportsService.keepOnlyChangedValuesOnQueryParams(where, this.toQuery(this.filter.defaultFilterValues));
    const url = this.router.serializeUrl(this.router.createUrlTree(['relatorios/avaliacoes'], { queryParams: { ...queryParams } }));
    const pathName = `${location.origin}${location.pathname}#${url}`;

    window.history.pushState(
      {},
      null,
      pathName
    );
  }

  async updateData(where): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBarService.show();

      await Promise.all([
        this.reportsService.getAccountRatingsCount(where),
        this.resolverService.getRatingsByQuantity(where),
        this.resolverService.getAccountRatingsAverageByTimeline(where),
        this.resolverService.getBestRatings(where),
        this.resolverService.getWorstRatings(where),
      ]).then((values) => {
        this.dashboardRating = values[0];
        this.ratingsByQuantity = values[1];
        this.lineChartData = values[2];
        this.accountRatingsAverageByTimeline = [this.chartDateIntervalSelectorMapperService.toLineChartFormat(
          this.lineChartData.accountRatingsAverageLineChart,
          this.lastOpenedAtResponse
        )];
        this.bestRatedTasks = values[3];
        this.worstRatedTasks  = values[4];
      }).finally(() => {
        this.isLoading = false;
        this.isLoadingOnlyResponseChart = false;

        this.byQuantityGraphTotalValue = this.ratingsByQuantity.reduce((accumulator, current) => {
          return accumulator + current.value;
        }, 0);
        this.averageByTimelineGraphTotalValue = this.ratingsByQuantity.reduce((accumulator, current) => {
          return accumulator + current.value;
        }, 0);
      });

    } catch (err) {
      sentry.captureException(err);
    } finally {
      this.fuseProgressBarService.hide();
    }
  }

  public getDateLineChartDateInterval(dateInterval: 'DAY' | 'WEEK' | 'MONTH'): void {
    this.lastOpenedAtResponse = dateInterval;

    if (this.filter.filterForm.invalid) {
      return;
    }

    this.updateLineChartData();
  }

  async updateLineChartData(): Promise<any> {
    try {
      this.isLoadingOnlyResponseChart = true;
      await delay(300);
      this.accountRatingsAverageByTimeline = [this.chartDateIntervalSelectorMapperService.toLineChartFormat(
        this.lineChartData.accountRatingsAverageLineChart,
        this.lastOpenedAtResponse
      )];
    } catch (err) {
      sentry.captureException(err);
    } finally {
      this.isLoadingOnlyResponseChart = false;
    }
  }

  toQuery(value: any): any {
    return {
      completedAt_gte: startOfDay(value.initialDate),
      completedAt_lte: endOfDay(value.finalDate),
      companyId_in: mapIds(value.companies),
      segmentId_in: mapIds(value.segments),
      locationId_in: mapIds(value.locations),
      locationGroupId_in: mapIds(value.locationGroups),
      maintenanceTypeId_in: mapIds(value.maintenanceTypes)
    };
  }

  reload(): void {
    this.filter.filterForm.updateValueAndValidity();
  }

  public autoRefresh(event: { checked: boolean }): void {
    if (event.checked) {
      this.intervalID = setInterval(() => this.reload(), this.autoReloadTime);
      return;
    }

    clearInterval(this.intervalID);
  }

  public clearFilters(): void {
    const pathName = `${location.origin}${location.pathname}#/relatorios/avaliacoes`;

    localStorage.removeItem(this.localStorageFilterValuesKey);
    window.history.pushState(
      {},
      null,
      pathName
    );
    this.filter.filterForm.patchValue(this.filter.defaultFilterValues);
  }

  async openMaintenanceDialog(id: string): Promise<void> {
    this.fuseProgressBarService.show();

    const maintenance = await this.maintenancesApi.searchOne(id).toPromise();
    this.fuseProgressBarService.hide();

    await this.matDialog.open(DialogEditMaintenanceComponent, {
      panelClass: 'dialog-edit-maintenance',
      data: {
        selectedTabIndex: 2,
        maintenance,
        isViewMode: !this.hasEditMaintenancePermission
      }
    })
      .afterClosed()
      .toPromise();
  }

  yAxisFormatToInteger(val: any): string {
    if (val % 1 > 0) {
      return '';
    }
    return val;
  }

  yAxisFormatToDecimal(val: any): string {
    if (val % 1 === 0) {
      return val + '.0';
    }
    return val;
  }
}
